// © Microsoft Corporation. All rights reserved.

import { OutlineButton } from "@microsoft/fast-components-react-msft";
import { getContextData } from "@msnews/core";
import { facebookLogin, googleLogin, logoImages, msaLogin, placeholderImages, addOrUpdateTelemetryObject, renderHybrid } from "@msnews/social-utils";
import React from "react";
import { renderToString } from "react-dom/server";
import { String } from "typescript-string-operations";
import { SocialProps } from "../Social.props";
import { multiLoginPageTelemetryActionDispatchers, multiLoginPageTelemetryContracts, MultiLoginPageTelemetryObjects } from "./MultiLoginPage.telemetry";

export default class MultiLoginPage extends React.Component<SocialProps> {
    containerRef: HTMLDivElement;
    telemetryObjects: MultiLoginPageTelemetryObjects;

    constructor(props) {
        super(props);
    }

    async loginWith(event, loginType: string, telemetryObject): Promise<void> {
        multiLoginPageTelemetryActionDispatchers.genericClick(event.currentTarget, telemetryObject);

        let email = null;
        try {
            if (loginType == "facebook") {
                email = await facebookLogin();
            } else if (loginType == "google") {
                email = await googleLogin();
            }
        } catch (e) {
        // Suppress the error
        }
        if (loginType != "microsoft" && !email) {
            return;
        } else if (loginType != "microsoft" && email) {
            this.props.popupConnector.openPopup("onboardingThirdPartyPage", { email, loginType, contentId: this.props.contentId });
            return;
        }

        try {
            const requestContext = this.props.requestContext;
            const locationHref = getContextData(requestContext.data.locationHref, "locationHref");
            await msaLogin(email, this.props.market, locationHref.split("?")[0]);
            this.props.usersConnector.fetchMe();
        } catch (e) {
        // Suppress the error
        }
    }

    componentDidUpdate(prevProps) {
        if (this.props.me.isSocialUser) {
            this.props.popupConnector.closePopup();
        } else if (this.props.me.name) {
            this.props.popupConnector.openPopup("onboardingPage", { contentId: this.props.contentId });
        }
    }

    render() {
        const { managedClasses, market, t, mainTelemetryObject, contentId, contents } = this.props;
        const cmsId = contentId && contents && contents[contentId] && contents[contentId].cmsId;

        /** Generate Telemetry Objects */
        const containerTelemetryObject= addOrUpdateTelemetryObject(mainTelemetryObject, multiLoginPageTelemetryContracts.main, { id: cmsId }, this.telemetryObjects && this.telemetryObjects.main);
        this.telemetryObjects = {
            main: containerTelemetryObject,
            loginMicrosoft: addOrUpdateTelemetryObject(containerTelemetryObject, multiLoginPageTelemetryContracts.loginMicrosoft, { id: cmsId }, this.telemetryObjects && this.telemetryObjects.loginMicrosoft),
            loginGoogle: addOrUpdateTelemetryObject(containerTelemetryObject, multiLoginPageTelemetryContracts.loginGoogle, { id: cmsId }, this.telemetryObjects && this.telemetryObjects.loginGoogle),
            loginFacebook: addOrUpdateTelemetryObject(containerTelemetryObject, multiLoginPageTelemetryContracts.loginFacebook, { id: cmsId }, this.telemetryObjects && this.telemetryObjects.loginFacebook)
        };

        const renderLoginImage = (image) => <img src={image} className="cwt-image"/>;

        return (
            <div className={managedClasses.multiLoginPageContainer} {...renderHybrid(this.telemetryObjects.main, true)}>
                <img alt={t.AriaLabelsProfilePicture} className="cwt-avatar" src={placeholderImages.avatar}/>
                <p className="cwt-title text-center"> {t.MultiLoginTitle} </p>
                <OutlineButton beforeContent={() => renderLoginImage(logoImages.microsoft)} className="cwt-link cwt-fast-button cwt-image-button" onClick={e => this.loginWith(e, "microsoft", this.telemetryObjects.loginMicrosoft)} {...renderHybrid(this.telemetryObjects.loginMicrosoft, false)}>
                    <span className="cwt-label">{t.MultiLoginMicrosoft}</span>
                </OutlineButton>
                <OutlineButton beforeContent={() => renderLoginImage(logoImages.google)} className="cwt-link cwt-fast-button cwt-image-button" onClick={e => this.loginWith(e, "google", this.telemetryObjects.loginGoogle)} {...renderHybrid(this.telemetryObjects.loginGoogle, false)}>
                    <span className="cwt-label">{t.MultiLoginGoogle}</span>
                </OutlineButton>
                <OutlineButton beforeContent={() => renderLoginImage(logoImages.facebook)} className="cwt-link cwt-fast-button cwt-image-button" onClick={ e => this.loginWith(e, "facebook", this.telemetryObjects.loginFacebook)} {...renderHybrid(this.telemetryObjects.loginFacebook, false)}>
                    <span className="cwt-label">{t.MultiLoginFacebook}</span>
                </OutlineButton>

                <span className="cwt-privacy-text text-center" dangerouslySetInnerHTML = {{ __html: String.Format(t.MultiLoginFooterTxt, {
                    MultiLoginFooterTerms: renderToString(<a href={`https://www.microsoft.com/${market}/servicesagreement`} className="cwt-link" target="_blank">{t.MultiLoginFooterTerms}</a>),
                    MultiLoginFooterPrivacy: renderToString(<a href={`https://privacy.microsoft.com/${market}/privacystatement`} className="cwt-link" target="_blank">{t.MultiLoginFooterPrivacy}</a>)
                })
                }}/>
            </div>
        );
    }
}
